/**
 * The app's TanStack QueryClient defaults.
 *
 * No automatic retries at this layer. The admin axios client retries reads
 * itself (GET/HEAD only, twice, ~2 s in total — see api/adminClient.ts), so a
 * React Query retry on top multiplies the wait before an error can show, and
 * a mutation retry re-sends a write that may already have landed. Queries
 * that bypass the admin client opt back into `retry: 1` individually in
 * queries/queryOptions.ts.
 *
 * Every query and mutation outcome also feeds lib/connectivity.ts, which
 * drives the "Can't reach Switchboard" banner: an error where the server
 * never answered raises it, and the next success clears it.
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { reportRequestError, reportRequestSuccess } from './connectivity';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: reportRequestError,
      onSuccess: reportRequestSuccess,
    }),
    mutationCache: new MutationCache({
      onError: reportRequestError,
      onSuccess: reportRequestSuccess,
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 0,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
